'use client';

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { useUser } from '@clerk/nextjs';
import { getApiBase } from '@/lib/apiBase';

export type ChartType = 'line' | 'candle';

interface ChartTypeContextType {
  chartType: ChartType;
  setChartType: (chartType: ChartType) => void;
  hasLoadedPreference: boolean;
}

const ChartTypeContext = createContext<ChartTypeContextType | undefined>(undefined);

export function ChartTypeProvider({ children }: { children: ReactNode }) {
  const { isSignedIn } = useUser();
  const [chartType, setChartTypeState] = useState<ChartType>('line');
  const [hasLoadedPreference, setHasLoadedPreference] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isSignedIn) {
      setHasLoadedPreference(false);
      setChartTypeState('line');
      return;
    }

    const loadPreference = async () => {
      try {
        const response = await fetch(`${getApiBase()}/api/preferences`);
        if (response.ok) {
          const data = await response.json();
          if (data.preferences?.chartType === 'line' || data.preferences?.chartType === 'candle') {
            setChartTypeState(data.preferences.chartType);
          }
        }
      } catch (error) {
        console.error('Failed to load chartType preference:', error);
      } finally {
        setHasLoadedPreference(true);
      }
    };

    loadPreference();
  }, [isSignedIn]);

  const savePreference = useCallback(async (newChartType: ChartType) => {
    if (!isSignedIn) return;

    try {
      await fetch(`${getApiBase()}/api/preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chartType: newChartType }),
      });
    } catch (error) {
      console.error('Failed to save chartType preference:', error);
    }
  }, [isSignedIn]);

  const setChartType = useCallback((newChartType: ChartType) => {
    setChartTypeState(newChartType);

    if (isSignedIn && hasLoadedPreference) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        savePreference(newChartType);
      }, 300);
    }
  }, [isSignedIn, hasLoadedPreference, savePreference]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <ChartTypeContext.Provider value={{ chartType, setChartType, hasLoadedPreference }}>
      {children}
    </ChartTypeContext.Provider>
  );
}

// Falls back to safe defaults when used outside a provider so isolated
// component tests can render charts without wrapping. The real provider is
// always present in the app via app/layout.tsx.
const FALLBACK: ChartTypeContextType = {
  chartType: 'line',
  setChartType: () => {},
  hasLoadedPreference: false,
};

export function useChartType() {
  const context = useContext(ChartTypeContext);
  return context ?? FALLBACK;
}
